import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import mentoria1 from "@/assets/mentoria-1.jpg";
import mentoria2 from "@/assets/mentoria-2.jpg";
import mentoria3 from "@/assets/mentoria-3.jpg";
import mentoria4 from "@/assets/mentoria-4.jpg";
import mentoria5 from "@/assets/mentoria-5.jpg";
import mentoria6 from "@/assets/mentoria-6.jpg";
import plataformaNota100 from "@/assets/plataforma-treinamento-nota-100.png.asset.json";
import coverIA from "@/assets/cover-ia-treinamento.jpg";
import coverTecnicasFechamento from "@/assets/cover-tecnicas-fechamento.jpg";
import { 
  ArrowRight, 
  Clock, 
  User, 
  Brain, 
  Lightbulb, 
  MessageSquare, 
  ThumbsUp,
  Zap,
  Trophy,
  Target,
  CheckCircle,
  ChevronRight,
  BookOpen,
  TrendingUp,
  Users,
  DollarSign,
  BarChart3,
  Wrench,
  Shield,
  Rocket,
  HeartHandshake,
  GraduationCap,
  Bot,
  Cpu,
  LineChart,
  AlertTriangle,
  Play
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const IAVendas = () => {
  const navigate = useNavigate();

  const iaCapabilities = [
    {
      number: "01",
      title: "Simulações de Negociação Realistas",
      subtitle: "Pratique com clientes virtuais que reagem como reais",
      description: "A inteligência artificial moderna consegue simular conversas de vendas incrivelmente realistas. Diferente de role-plays tradicionais, onde um colega ou gestor faz o papel de cliente, a IA não tem vieses, não cansa e pode simular milhares de perfis diferentes de compradores. Isso significa que seu vendedor pode treinar com um CEO de multinacional às 10h, um comprador técnico às 11h e um cliente resistente às 14h - tudo no mesmo dia.",
      benefits: [
        "Disponibilidade 24/7 para prática",
        "Centenas de perfis de clientes diferentes",
        "Sem constrangimento de errar na frente de colegas",
        "Repetição ilimitada de cenários desafiadores"
      ],
      icon: Bot
    },
    {
      number: "02", 
      title: "Feedback Instantâneo e Personalizado",
      subtitle: "Saiba exatamente o que melhorar em cada interação",
      description: "Um dos maiores desafios do treinamento tradicional é a subjetividade do feedback. O gestor pode não lembrar de todos os detalhes da simulação, ou pode ter preferências pessoais que influenciam sua avaliação. A IA, por outro lado, analisa cada palavra, cada pausa, cada argumento usado. Ela identifica padrões que olhos humanos perderiam e oferece sugestões específicas e acionáveis.",
      benefits: [
        "Análise objetiva e consistente",
        "Identificação de padrões de erro",
        "Sugestões práticas e específicas",
        "Acompanhamento de evolução ao longo do tempo"
      ],
      icon: BarChart3
    },
    {
      number: "03",
      title: "Personalização do Treinamento",
      subtitle: "Cada vendedor recebe exatamente o que precisa",
      description: "Nem todo vendedor precisa melhorar nas mesmas áreas. Alguns são excelentes em prospecção, mas fracos em fechamento. Outros dominam objeções de preço, mas travam em objeções técnicas. A IA consegue diagnosticar os pontos fracos de cada profissional e criar trilhas de desenvolvimento personalizadas, focando tempo e energia onde realmente faz diferença.",
      benefits: [
        "Diagnóstico individual de competências",
        "Trilhas de aprendizado adaptativas",
        "Foco nos gaps específicos",
        "Otimização do tempo de treinamento"
      ],
      icon: Target
    },
    {
      number: "04",
      title: "Análise de Calls Reais",
      subtitle: "Transforme ligações em oportunidades de aprendizado",
      description: "A inteligência artificial pode analisar gravações de ligações reais de vendas, identificando momentos-chave onde o vendedor poderia ter usado técnicas diferentes. Isso permite que gestores identifiquem padrões em toda a equipe e que vendedores aprendam com suas próprias interações, não apenas com cenários hipotéticos.",
      benefits: [
        "Análise de ligações em escala",
        "Identificação de melhores práticas internas",
        "Coaching baseado em situações reais",
        "Benchmarking entre equipe"
      ],
      icon: MessageSquare
    },
    {
      number: "05",
      title: "Gamificação Inteligente",
      subtitle: "Competição saudável que acelera resultados",
      description: "A IA pode criar sistemas de gamificação que vão muito além de simples pontuações. Com algoritmos adaptativos, ela equilibra desafios para que tanto novatos quanto veteranos se sintam desafiados. Rankings dinâmicos, conquistas personalizadas e missões adaptadas ao nível de cada um mantêm a equipe engajada e motivada a evoluir continuamente.",
      benefits: [
        "Rankings e competições equilibradas",
        "Conquistas e recompensas progressivas",
        "Desafios adaptados ao nível individual",
        "Engajamento contínuo da equipe"
      ],
      icon: Trophy
    },
    {
      number: "06",
      title: "Predição de Performance",
      subtitle: "Antecipe problemas antes que afetem resultados",
      description: "Com machine learning, é possível analisar os padrões de evolução de cada vendedor e prever quem está em risco de não bater metas, quem precisa de atenção especial e quem está pronto para novos desafios. Isso permite intervenções proativas, evitando que problemas pequenos se tornem grandes.",
      benefits: [
        "Identificação precoce de riscos",
        "Intervenção proativa de gestores",
        "Alocação inteligente de recursos",
        "Previsibilidade de resultados"
      ],
      icon: LineChart
    }
  ];

  const implementationSteps = [
    {
      step: "1",
      title: "Diagnóstico Inicial",
      description: "Antes de implementar qualquer ferramenta de IA, é fundamental fazer um diagnóstico completo da sua operação comercial. Quais são os principais gargalos? Onde os vendedores mais erram? Quais métricas precisam melhorar? Um diagnóstico bem feito garante que a IA será usada para resolver problemas reais, não apenas para parecer inovador.",
      icon: Target
    },
    {
      step: "2",
      title: "Escolha da Ferramenta",
      description: "Nem toda solução de IA é adequada para toda empresa. Considere fatores como: tamanho da equipe, tipo de venda (B2B, B2C, transacional, consultiva), orçamento disponível, integrações necessárias com CRM e outras ferramentas. Uma ferramenta cara demais pode inviabilizar o projeto; uma simples demais pode não entregar resultados.",
      icon: Lightbulb
    },
    {
      step: "3",
      title: "Piloto Controlado",
      description: "Não implemente para toda a equipe de uma vez. Selecione um grupo piloto, preferencialmente misto (veteranos e novatos), e rode por 30-60 dias. Meça resultados, colete feedback, ajuste processos. Isso reduz riscos e gera aprendizados valiosos para a expansão.",
      icon: Play
    },
    {
      step: "4",
      title: "Rollout Gradual",
      description: "Com os aprendizados do piloto, expanda gradualmente. Treine gestores primeiro, depois líderes de equipe, depois vendedores. Crie materiais de suporte, FAQ, e canais para tirar dúvidas. Uma implementação bem planejada evita resistências e acelera adoção.",
      icon: Rocket
    },
    {
      step: "5",
      title: "Otimização Contínua",
      description: "A implementação não termina no rollout. Analise dados regularmente, ajuste parâmetros da IA, adicione novos cenários de treinamento, incorpore feedback da equipe. A melhoria contínua é o que separa empresas que extraem valor máximo da tecnologia daquelas que apenas gastaram dinheiro.",
      icon: TrendingUp
    }
  ];

  const commonObjections = [
    {
      objection: "IA não consegue substituir o treinamento presencial",
      response: "Você está certo que o presencial tem valor - e a IA não pretende substituí-lo completamente. O que ela faz é complementar e potencializar. Enquanto treinamentos presenciais podem acontecer mensalmente ou trimestralmente, a IA permite prática diária. É como comparar aula de inglês (presencial) com app de idiomas (IA): um não substitui o outro, mas juntos aceleram o aprendizado."
    },
    {
      objection: "Minha equipe vai resistir a usar tecnologia",
      response: "Resistência à mudança é natural. A chave é mostrar benefício imediato para o vendedor, não apenas para a empresa. Quando ele percebe que a IA o ajuda a bater metas (e consequentemente ganhar mais comissão), a resistência desaparece. Comece com vendedores mais abertos à tecnologia e deixe o sucesso deles convencer os outros."
    },
    {
      objection: "É muito caro para o tamanho da minha empresa",
      response: "Compare o custo da ferramenta com o custo de não treinar: vendas perdidas, rotatividade alta, ramp-up lento de novos vendedores. Muitas empresas descobrem que o ROI de treinamento com IA é positivo já no primeiro trimestre. E existem soluções para diferentes portes de empresa - desde startups até grandes corporações."
    },
    {
      objection: "Já temos um programa de treinamento que funciona",
      response: "Se funciona, excelente! A IA pode torná-lo ainda melhor. Use-a para reforçar conceitos entre sessões presenciais, para prática adicional, para onboarding de novos vendedores. Não é sobre substituir o que funciona, é sobre amplificar os resultados que você já tem."
    }
  ];

  const statistics = [
    { value: "67%", label: "das empresas ainda usam apenas treinamento presencial tradicional", icon: Users },
    { value: "3x", label: "mais rápido é o ramp-up de novos vendedores com IA", icon: Rocket },
    { value: "45%", label: "de aumento médio em taxa de conversão após 90 dias", icon: TrendingUp },
    { value: "R$12k", label: "economia média por vendedor/ano em custos de treinamento", icon: DollarSign }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Como a IA Está Revolucionando o Treinamento de Equipes Comerciais",
    "description": "Entenda como a inteligência artificial pode acelerar o desenvolvimento de vendedores, reduzir custos de treinamento e aumentar resultados comerciais.",
    "author": {
      "@type": "Person",
      "name": "Junior Bianchim"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Vendas Trainer"
    },
    "datePublished": "2025-09-18",
    "dateModified": "2025-09-18",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://vendastrainer.com/posts/ia-treinamento-vendas"
    }
  };

  return (
    <>
      <SEOHead 
        title="Como a IA Está Revolucionando o Treinamento de Equipes Comerciais"
        description="Entenda como a inteligência artificial pode acelerar o desenvolvimento de vendedores, reduzir custos de treinamento e aumentar resultados comerciais em até 45%."
        keywords="inteligência artificial vendas, treinamento com IA, IA para vendedores, treinamento equipe comercial, tecnologia em vendas, automação comercial"
        canonicalUrl="https://vendastrainer.com/posts/ia-treinamento-vendas"
        ogType="article"
        articlePublishedTime="2024-12-18T00:00:00Z"
        articleAuthor="Junior Bianchim"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10">
        <Header />

        <main id="main-content">
          <article>
            {/* Hero Section - Full Width */}
            <header className="relative py-12 sm:py-20 md:py-32 overflow-hidden">
              <div 
                className="absolute inset-0 bg-cover bg-center opacity-20"
                style={{ 
                  backgroundImage: `url(${coverIA})` 
                }}
                role="img"
                aria-label="Imagem de fundo representando inteligência artificial e tecnologia"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
              
              <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/50 bg-primary/10 backdrop-blur-sm">
                      <Brain className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span className="text-sm font-medium text-primary">Tecnologia</span>
                    </span>
                  </div>
                  
                  <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold leading-tight mb-6">
                    <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                      Como a IA Está Revolucionando
                    </span>
                    <br />
                    <span className="text-foreground">o Treinamento de Equipes Comerciais</span>
                  </h1>
                  
                  <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                    Descubra como a inteligência artificial pode acelerar o desenvolvimento de vendedores, 
                    reduzir custos de treinamento e aumentar resultados comerciais de forma mensurável.
                  </p>

                  <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span>Por <strong className="text-foreground">Junior Bianchim</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" aria-hidden="true" />
                      <time dateTime="2025-09-18">18 Set 2025</time>
                      <span>• 12 min de leitura</span>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            {/* Intro Section - 2 Columns */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="intro-heading">
              <div className="container mx-auto px-4">
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center">
                  <div className="flex-1 order-2 lg:order-1">
                    <h2 id="intro-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6">
                      O Problema do Treinamento Tradicional de Vendas
                    </h2>
                    <p className="text-muted-foreground mb-4 text-base sm:text-lg">
                      Durante décadas, o treinamento de equipes comerciais seguiu um modelo previsível: 
                      workshops presenciais, role-plays com colegas, observação de vendedores seniores e 
                      feedbacks esporádicos dos gestores. Esse modelo tem seus méritos, mas também 
                      <strong className="text-foreground"> limitações significativas</strong>.
                    </p>
                    <p className="text-muted-foreground mb-4 text-base sm:text-lg">
                      Primeiro, há a questão da escalabilidade. Treinar 10 vendedores é uma coisa; 
                      treinar 100 ou 1.000 mantendo a mesma qualidade é praticamente impossível 
                      sem custos proibitivos. Segundo, há o problema da consistência: cada gestor 
                      tem seu estilo, suas preferências, seus vieses. O que um considera uma 
                      abordagem excelente, outro pode criticar.
                    </p>
                    <p className="text-muted-foreground mb-6 text-base sm:text-lg">
                      Terceiro, e talvez mais crítico, há a questão do timing. Vendedores precisam 
                      de prática no momento em que enfrentam desafios, não semanas depois em um 
                      workshop agendado. A memória é curta, e habilidades não praticadas se 
                      atrofiam rapidamente.
                    </p>
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-card border border-primary/20">
                      <AlertTriangle className="w-6 h-6 text-primary flex-shrink-0" aria-hidden="true" />
                      <p className="text-sm text-muted-foreground">
                        <strong className="text-foreground">Fato alarmante:</strong> 84% do conteúdo de treinamentos 
                        tradicionais é esquecido em apenas 90 dias, segundo pesquisa da Harvard Business Review.
                      </p>
                    </div>
                  </div>
                  <div className="flex-1 order-1 lg:order-2">
                    <figure>
                      <img 
                        src={mentoria2}
                        alt="Equipe de vendas em treinamento presencial com Junior Bianchim"
                        className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
                        loading="lazy"
                      />
                      <figcaption className="text-center text-sm text-muted-foreground mt-3">
                        Treinamentos presenciais são valiosos, mas têm limitações de escala e frequência
                      </figcaption>
                    </figure>
                  </div>
                </div>
              </div>
            </section>

            {/* Statistics Section - 4 Columns Grid */}
            <section className="py-12 sm:py-16" aria-labelledby="stats-heading">
              <div className="container mx-auto px-4">
                <h2 id="stats-heading" className="sr-only">Estatísticas sobre IA em treinamento de vendas</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                  {statistics.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                      <Card 
                        key={index}
                        className="p-4 sm:p-6 text-center bg-card/50 backdrop-blur-sm border-primary/10 hover:border-primary/30 transition-colors"
                      >
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-3 sm:mb-4">
                          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" aria-hidden="true" />
                        </div>
                        <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary mb-2">{stat.value}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground">{stat.label}</p>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* O que é IA em Vendas - Full Width Content */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="what-is-ai-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="text-center mb-10 sm:mb-16">
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-accent/50 bg-accent/10 mb-4">
                      <Cpu className="w-4 h-4 text-accent" aria-hidden="true" />
                      <span className="text-sm font-medium text-accent">Conceitos Fundamentais</span>
                    </span>
                    <h2 id="what-is-ai-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                      O Que Realmente é "IA" no Contexto de Vendas?
                    </h2>
                    <p className="text-muted-foreground text-base sm:text-lg">
                      Antes de explorar as aplicações, vamos desmistificar o conceito
                    </p>
                  </div>

                  <div className="prose prose-lg max-w-none">
                    <p className="text-muted-foreground text-base sm:text-lg mb-6">
                      Quando falamos de inteligência artificial em treinamento de vendas, não estamos 
                      falando de robôs humanoides ou de um computador consciente. Estamos falando de 
                      <strong className="text-foreground"> algoritmos sofisticados</strong> que conseguem 
                      processar grandes volumes de dados, identificar padrões e simular interações humanas 
                      de forma cada vez mais convincente.
                    </p>

                    <div className="grid sm:grid-cols-2 gap-6 my-8">
                      <Card className="p-5 sm:p-6 bg-card border-primary/20">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Brain className="w-5 h-5 text-primary" aria-hidden="true" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg mb-2">Machine Learning</h3>
                            <p className="text-sm text-muted-foreground">
                              Algoritmos que aprendem com dados históricos para fazer previsões e 
                              recomendações cada vez mais precisas. Quanto mais dados, melhor o aprendizado.
                            </p>
                          </div>
                        </div>
                      </Card>
                      <Card className="p-5 sm:p-6 bg-card border-accent/20">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="w-5 h-5 text-accent" aria-hidden="true" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg mb-2">Processamento de Linguagem Natural</h3>
                            <p className="text-sm text-muted-foreground">
                              Tecnologia que permite à máquina entender e gerar texto humano, 
                              viabilizando chatbots e análise de conversas.
                            </p>
                          </div>
                        </div>
                      </Card>
                    </div>

                    <p className="text-muted-foreground text-base sm:text-lg mb-6">
                      A combinação dessas tecnologias permite criar experiências de treinamento que até 
                      poucos anos atrás seriam consideradas ficção científica. Imagine um vendedor praticando 
                      uma negociação com um cliente virtual que responde em tempo real, faz objeções 
                      realistas, muda de humor conforme a conversa evolui e, ao final, oferece uma 
                      análise detalhada de cada decisão tomada.
                    </p>

                    <p className="text-muted-foreground text-base sm:text-lg mb-6">
                      Isso não é o futuro. <strong className="text-foreground">Isso já é possível hoje</strong>. 
                      E empresas que estão adotando essas tecnologias estão vendo vantagens competitivas 
                      significativas: vendedores que atingem produtividade máxima mais rápido, taxas de 
                      conversão mais altas, e custos de treinamento dramaticamente reduzidos.
                    </p>

                    <blockquote className="border-l-4 border-primary pl-6 py-4 my-8 bg-primary/5 rounded-r-lg">
                      <p className="text-lg italic text-foreground mb-2">
                        "A inteligência artificial não vai substituir vendedores. Mas vendedores que 
                        usam IA vão substituir aqueles que não usam."
                      </p>
                      <footer className="text-sm text-muted-foreground">
                        — Tendência observada em pesquisa do Gartner, 2024
                      </footer>
                    </blockquote>
                  </div>
                </div>
              </div>
            </section>

            {/* IA Capabilities - Alternating Layout */}
            <section className="py-12 sm:py-20" aria-labelledby="capabilities-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-10 sm:mb-16">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/50 bg-primary/10 mb-4">
                    <Wrench className="w-4 h-4 text-primary" aria-hidden="true" />
                    <span className="text-sm font-medium text-primary">Aplicações Práticas</span>
                  </span>
                  <h2 id="capabilities-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      6 Formas Como a IA Transforma
                    </span>
                    <br />
                    o Treinamento de Vendas
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
                    Da simulação de negociações à predição de performance, conheça as aplicações 
                    mais impactantes da inteligência artificial em desenvolvimento comercial.
                  </p>
                </div>

                <div className="space-y-16 sm:space-y-24">
                  {iaCapabilities.map((capability, index) => {
                    const Icon = capability.icon;
                    const isEven = index % 2 === 0;
                    
                    return (
                      <div 
                        key={capability.number}
                        className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-16 items-center`}
                      >
                        <div className="flex-1 w-full">
                          <div className="flex items-center gap-4 mb-4">
                            <span className="text-5xl sm:text-7xl font-bold text-primary/20">{capability.number}</span>
                            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
                              <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" aria-hidden="true" />
                            </div>
                          </div>
                          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">{capability.title}</h3>
                          <p className="text-base sm:text-lg text-primary mb-4">{capability.subtitle}</p>
                          <p className="text-muted-foreground mb-6 text-base sm:text-lg leading-relaxed">
                            {capability.description}
                          </p>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {capability.benefits.map((benefit, i) => (
                              <div 
                                key={i}
                                className="flex items-center gap-2 p-3 rounded-lg bg-card border border-primary/10"
                              >
                                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" aria-hidden="true" />
                                <span className="text-sm">{benefit}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex-1 w-full">
                          <figure>
                            <img 
                              src={[plataformaNota100.url, mentoria2, mentoria3, mentoria6, mentoria2, mentoria3][index]}
                              alt={`${capability.title} — plataforma e mentorias Bianchim Business`}
                              className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
                              loading="lazy"
                            />
                          </figure>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* CTA Colorida Section */}
            <section className="py-12 sm:py-20" aria-labelledby="cta-experience">
              <div className="container mx-auto px-4">
                <Card className="p-6 sm:p-12 md:p-16 bg-gradient-to-br from-primary via-accent to-secondary border-0 text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url(${coverIA})` }} />
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-accent/95 to-secondary/95" />
                  
                  <div className="relative z-10">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6 animate-pulse">
                      <Bot className="w-8 h-8 sm:w-10 sm:h-10 text-white" aria-hidden="true" />
                    </div>
                    <h2 id="cta-experience" className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 text-white">
                      Experimente a IA na Prática
                    </h2>
                    <p className="text-base sm:text-xl text-white/90 max-w-2xl mx-auto mb-8">
                      Nada substitui a experiência. Veja como é treinar com inteligência artificial 
                      e descubra por que empresas líderes estão migrando para esse modelo.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        size="lg"
                        onClick={() => navigate("/auth")}
                        className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6 shadow-2xl"
                      >
                        Começar Simulação Grátis
                        <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                      </Button>
                      <Button
                        size="lg"
                        variant="outline"
                        onClick={() => navigate("/#pricing")}
                        className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
                      >
                        Ver Planos e Preços
                      </Button>
                    </div>
                    <div className="flex flex-wrap justify-center gap-4 sm:gap-8 mt-6 text-sm text-white/80">
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" aria-hidden="true" />
                        10 simulações grátis
                      </span>
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" aria-hidden="true" />
                        Sem cartão de crédito
                      </span>
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" aria-hidden="true" />
                        Setup em 2 minutos
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
            </section>

            {/* Implementation Steps - 3 Columns then Full */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="implementation-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-10 sm:mb-16">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-accent/50 bg-accent/10 mb-4">
                    <Rocket className="w-4 h-4 text-accent" aria-hidden="true" />
                    <span className="text-sm font-medium text-accent">Guia de Implementação</span>
                  </span>
                  <h2 id="implementation-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                    Como Implementar IA no Treinamento da Sua Equipe
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
                    Um roadmap prático em 5 passos para transformar o desenvolvimento 
                    da sua equipe comercial com inteligência artificial.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {implementationSteps.slice(0, 3).map((step) => {
                    const Icon = step.icon;
                    return (
                      <Card 
                        key={step.step}
                        className="p-6 bg-card border-primary/10 hover:border-primary/30 transition-all hover:-translate-y-1"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <span className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold">
                            {step.step}
                          </span>
                          <Icon className="w-5 h-5 text-primary" aria-hidden="true" />
                        </div>
                        <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      </Card>
                    );
                  })}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {implementationSteps.slice(3).map((step) => {
                    const Icon = step.icon;
                    return (
                      <Card 
                        key={step.step}
                        className="p-6 bg-card border-accent/10 hover:border-accent/30 transition-all hover:-translate-y-1"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <span className="w-10 h-10 rounded-full bg-gradient-to-br from-accent to-secondary flex items-center justify-center text-white font-bold">
                            {step.step}
                          </span>
                          <Icon className="w-5 h-5 text-accent" aria-hidden="true" />
                        </div>
                        <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Objections Section - 2 Columns */}
            <section className="py-12 sm:py-20" aria-labelledby="objections-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-10 sm:mb-16">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/50 bg-primary/10 mb-4">
                    <Shield className="w-4 h-4 text-primary" aria-hidden="true" />
                    <span className="text-sm font-medium text-primary">Objeções Comuns</span>
                  </span>
                  <h2 id="objections-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                    Respondendo às Dúvidas Mais Frequentes
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
                    Gestores e empresários frequentemente têm objeções sobre IA em treinamento. 
                    Veja como respondê-las com dados e argumentos sólidos.
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                  {commonObjections.map((item, index) => (
                    <Card 
                      key={index}
                      className="p-6 sm:p-8 bg-card border-primary/10"
                    >
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                          <AlertTriangle className="w-5 h-5 text-red-500" aria-hidden="true" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-foreground">"{item.objection}"</h3>
                        </div>
                      </div>
                      <div className="pl-14">
                        <div className="w-full h-px bg-border mb-4" />
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                            {item.response}
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* Deep Content Section */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="deep-content-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 id="deep-content-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 text-center">
                    O Futuro do Treinamento Comercial Já Começou
                  </h2>

                  <div className="prose prose-lg max-w-none">
                    <p className="text-muted-foreground text-base sm:text-lg mb-6">
                      Estamos em um ponto de inflexão na história do desenvolvimento profissional em vendas. 
                      As empresas que entenderem isso e agirem agora terão vantagens competitivas 
                      significativas nos próximos anos. As que esperarem demais terão que correr 
                      atrás de concorrentes que já estarão à frente.
                    </p>

                    <p className="text-muted-foreground text-base sm:text-lg mb-6">
                      Pense nisso: enquanto você leu este artigo, vendedores em empresas concorrentes 
                      podem estar praticando com IA, recebendo feedback personalizado, desenvolvendo 
                      habilidades específicas para fechar mais negócios. Cada dia de atraso é uma 
                      oportunidade perdida.
                    </p>

                    <div className="my-8 p-6 sm:p-8 rounded-2xl bg-gradient-to-r from-primary/10 via-accent/10 to-secondary/10 border border-primary/20">
                      <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
                        <GraduationCap className="w-6 h-6 text-primary" aria-hidden="true" />
                        Tendências para os Próximos Anos
                      </h3>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <ChevronRight className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                          <span className="text-muted-foreground">
                            <strong className="text-foreground">2025:</strong> IA conversacional indistinguível 
                            de humanos em 80% dos cenários de role-play
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <ChevronRight className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                          <span className="text-muted-foreground">
                            <strong className="text-foreground">2026:</strong> Integração total entre CRM, 
                            treinamento e análise preditiva de performance
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <ChevronRight className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                          <span className="text-muted-foreground">
                            <strong className="text-foreground">2027:</strong> Coaching em tempo real durante 
                            ligações de vendas (IA sussurrando sugestões)
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <ChevronRight className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                          <span className="text-muted-foreground">
                            <strong className="text-foreground">2028:</strong> Avatares de IA conduzindo 
                            primeiras etapas do funil de vendas de forma autônoma
                          </span>
                        </li>
                      </ul>
                    </div>

                    <p className="text-muted-foreground text-base sm:text-lg mb-6">
                      A pergunta não é se sua empresa vai adotar IA no treinamento de vendas, 
                      mas <strong className="text-foreground">quando</strong>. E quanto mais cedo 
                      for essa adoção, maior será a curva de aprendizado acumulada quando a 
                      tecnologia se tornar mainstream.
                    </p>

                    <p className="text-muted-foreground text-base sm:text-lg mb-6">
                      Lembre-se: a mesma resistência que existiu quando empresas começaram a usar 
                      CRMs, quando passaram a fazer cold calling, quando adotaram automação de 
                      marketing - essa resistência existe hoje com IA. E, assim como naqueles 
                      casos, os early adopters colheram os maiores frutos.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Author Section */}
            <section className="py-12 sm:py-16" aria-labelledby="author-heading">
              <div className="container mx-auto px-4">
                <Card className="p-6 sm:p-8 bg-card max-w-3xl mx-auto">
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                    <img 
                      src={juniorBianchim}
                      alt="Foto de Junior Bianchim, autor do artigo e especialista em vendas"
                      className="w-24 h-24 rounded-full object-cover border-4 border-primary/20"
                      loading="lazy"
                    />
                    <div className="text-center sm:text-left">
                      <h3 id="author-heading" className="text-xl font-bold mb-1">Junior Bianchim</h3>
                      <p className="text-primary text-sm mb-3">Fundador do Bianchim Business</p>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        Vendedor com mais de 20 anos de experiência em empresas como Sênior Sistemas, 
                        uma das maiores empresas de software do Brasil. Atualmente é professor de 
                        Vendas, Comércio e Administração no SENAC, e dedica seu tempo livre a 
                        desenvolver soluções que democratizem o acesso a treinamento de vendas de 
                        alta qualidade através de tecnologia.
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </section>

            {/* Related Posts - 2 Horizontal */}
            <section className="py-12 sm:py-16 bg-muted/30" aria-labelledby="related-heading">
              <div className="container mx-auto px-4">
                <h2 id="related-heading" className="text-xl sm:text-2xl font-bold mb-6 sm:mb-8">
                  Artigos Relacionados
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card 
                    className="p-4 sm:p-6 bg-card hover:border-primary/50 transition-all cursor-pointer group"
                    onClick={() => navigate("/posts/7-tecnicas-fechamento")}
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && navigate("/posts/7-tecnicas-fechamento")}
                  >
                    <div className="flex flex-col sm:flex-row gap-4">
                      <img 
                        src={coverTecnicasFechamento}
                        alt="Junior Bianchim em mentoria sobre técnicas de fechamento"
                        className="w-full sm:w-32 h-32 sm:h-24 object-cover rounded-lg"
                        loading="lazy"
                      />
                      <div className="flex-1">
                        <span className="text-xs text-primary font-medium">Técnicas de Vendas</span>
                        <h3 className="font-semibold mt-1 group-hover:text-primary transition-colors">
                          7 Técnicas de Fechamento que Todo Vendedor Precisa Dominar
                        </h3>
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          Aprenda as técnicas de fechamento mais eficazes para aumentar sua taxa de conversão.
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 sm:p-6 bg-card hover:border-accent/50 transition-all cursor-pointer group opacity-70">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="w-full sm:w-32 h-32 sm:h-24 rounded-lg bg-accent/20 flex items-center justify-center">
                        <DollarSign className="w-8 h-8 text-accent" aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs text-accent font-medium">Objeções</span>
                        <h3 className="font-semibold mt-1">
                          Como Vencer Objeções de Preço Sem Dar Desconto
                        </h3>
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          Em breve: estratégias para demonstrar valor e fechar vendas sem sacrificar margem.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* Final Urgent CTA */}
            <section className="py-16 sm:py-24" aria-labelledby="final-cta">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/30 mb-6">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                    </span>
                    <span className="text-sm font-medium text-red-500">Oferta por Tempo Limitado</span>
                  </div>
                  
                  <h2 id="final-cta" className="text-2xl sm:text-4xl md:text-5xl font-bold mb-6">
                    Sua Equipe Está Treinando
                    <br />
                    <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                      Enquanto Você Lê Isso?
                    </span>
                  </h2>
                  
                  <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                    Cada dia sem treinamento eficiente é dinheiro deixado na mesa. 
                    Comece agora e veja resultados em menos de 30 dias.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                    <Button
                      size="lg"
                      onClick={() => navigate("/auth")}
                      className="bg-gradient-to-r from-primary via-accent to-secondary hover:opacity-90 text-lg px-10 py-7 shadow-2xl animate-pulse"
                    >
                      <Zap className="mr-2 w-5 h-5" aria-hidden="true" />
                      Começar Agora - É Grátis
                    </Button>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-8 text-sm text-muted-foreground">
                    <span className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary" aria-hidden="true" />
                      10 simulações grátis
                    </span>
                    <span className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary" aria-hidden="true" />
                      Sem cartão de crédito
                    </span>
                    <span className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary" aria-hidden="true" />
                      Cancele quando quiser
                    </span>
                    <span className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary" aria-hidden="true" />
                      Suporte em português
                    </span>
                  </div>

                  <p className="mt-8 text-sm text-muted-foreground">
                    Mais de <strong className="text-foreground">500 empresas</strong> já transformaram 
                    seus resultados com o Vendas Trainer
                  </p>
                </div>
              </div>
            </section>
          </article>
        </main>

        <PageNavigation />
        <Footer />
      </div>
    </>
  );
};

export default IAVendas;
